import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import Navbar from "./Menu/Navbar";
import SidebarDrawer, {
  DRAWER_WIDTH,
  LAYOUT_NAVBAR_HEIGHT,
} from "./Menu/SidebarDrawer";
import { VM_Colors } from "./Styles/VMStyles";
import FCMListener from "./Util/FCM/FCMListener";
import StorageFactory from "./Util/StorageFactory";
import { clearAuthSession } from "./Util/authSessionHelper";
import {
  clearSubscription,
  clearUserNotFoundMessage,
  fetchSubscriptionStatus,
} from "./Redux/slices/subscriptionSlice";
import {
  clearSubscriptionTier,
  fetchSubscriptionTierStatus,
} from "./Redux/slices/subscriptionTierSlice";
import UserNotFoundDialog from "./Components/UserNotFoundDialog";
import { OUTLET_CONTENT_MAX_WIDTH, ROUTE_DELETE_ACCOUNT, ROUTE_LOGIN, SESSION_ACCOUNT_DELETION_DATA_KEY, SESSION_ACCOUNT_DELETION_PENDING_KEY, SESSION_ADMIN_TYPE_KEY, SESSION_IS_SUPER_ADMIN_KEY, SESSION_USER_TYPE_KEY } from "./Util/VMConstants";
import { isAdminLoggedIn } from "./Util/UtilHelper";
import {
  ACCOUNT_DELETION_STATUS,
  clearAccountDeletionSession,
  getAccountDeleteRequestStatus,
  hasAccountDeletionPending,
  isPendingDeletionStatus,
  setAccountDeletionSession,
} from "./Services/accountDeletionService";

/** Outlet width; horizontal scroll appears when inner content is wider. Swap for e.g. '500px' or 'min(100%, 500px)' if you prefer a pixel cap. */

function App() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const isSuperAdminRoute = location.pathname.startsWith("/superAdmin");
  const showLayout = !isSuperAdminRoute;

  const dispatch = useDispatch();
  const userNotFoundMessage = useSelector(
    (state) => state.subscription.userNotFoundMessage
  );

  /* ---------------- STORAGE ---------------- */
  const sessionFactory = useMemo(
    () => new StorageFactory("session"),
    []
  );

  const [userID, setUserID] = useState(null);

  /* ---------------- TRACK FETCH PER LOGIN ---------------- */
  const lastFetchedUserRef = useRef(null);
  const lastDeletionCheckUserRef = useRef(null);
  const userNotFoundHandlingRef = useRef(false);

  const handleUserNotFoundConfirm = useCallback(async () => {
    if (userNotFoundHandlingRef.current) return;
    userNotFoundHandlingRef.current = true;

    dispatch(clearUserNotFoundMessage());
    dispatch(clearSubscription());
    dispatch(clearSubscriptionTier());
    lastFetchedUserRef.current = null;
    lastDeletionCheckUserRef.current = null;
    setUserID(null);

    await clearAuthSession();

    userNotFoundHandlingRef.current = false;
    navigate(`/${ROUTE_LOGIN}`, { replace: true });
  }, [dispatch, navigate]);

  /* ---------------- READ USER ID (LOGIN / LOGOUT AWARE) ---------------- */
  useEffect(() => {
    const readUser = () => {
      setUserID(sessionFactory.get("user_id"));
    };

    readUser(); // initial read

    const onStorage = () => readUser();
    window.addEventListener("storage", onStorage);

    return () => window.removeEventListener("storage", onStorage);
  }, [sessionFactory]);

  /* ---------------- FETCH SUBSCRIPTION (ONCE PER LOGIN) ---------------- */
  useEffect(() => {
    if (!userID) {
      // logout → reset
      lastFetchedUserRef.current = null;
      lastDeletionCheckUserRef.current = null;
      return;
    }

    const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
    const adminType = sessionFactory.get(SESSION_ADMIN_TYPE_KEY);
    const isAdminPanel =
      sessionFactory.get(SESSION_IS_SUPER_ADMIN_KEY) === true ||
      adminType === "SuperAdmin" ||
      adminType === "subAdmin" ||
      userType === "SuperAdmin" ||
      userType === "subAdmin";
    if (isAdminPanel) {
      return;
    }

    // already fetched for this login
    if (lastFetchedUserRef.current === userID) {
      return;
    }

    lastFetchedUserRef.current = userID;

    const loadSubscription = async () => {
      try {
        await dispatch(fetchSubscriptionStatus(userID)).unwrap();
        dispatch(fetchSubscriptionTierStatus(userID));
      } catch (payload) {
        if (payload?.userNotFound) {
          lastFetchedUserRef.current = null;
        }
      }
    };

    loadSubscription();
  }, [dispatch, userID, sessionFactory]);

  /* ---------------- ACCOUNT DELETION: REDIRECT FROM SESSION (NO NETWORK) ---------------- */
  useEffect(() => {
    if (!userID || !showLayout) return;
    if (location.pathname.includes(ROUTE_DELETE_ACCOUNT)) return;

    const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
    const adminType = sessionFactory.get(SESSION_ADMIN_TYPE_KEY);
    const isAdminPanel =
      sessionFactory.get(SESSION_IS_SUPER_ADMIN_KEY) === true ||
      adminType === "SuperAdmin" ||
      adminType === "subAdmin" ||
      userType?.toLowerCase().trim() === "superadmin" || 
      userType === "subAdmin";

    if (!isAdminLoggedIn(userType) || isAdminPanel) return;

    if (hasAccountDeletionPending(sessionFactory)) {
      navigate(`/${ROUTE_DELETE_ACCOUNT}`, { replace: true });
      return;
    }

    const cachedDeletion = sessionFactory.get(SESSION_ACCOUNT_DELETION_DATA_KEY);
    if (cachedDeletion && isPendingDeletionStatus(cachedDeletion.status)) {
      navigate(`/${ROUTE_DELETE_ACCOUNT}`, { replace: true });
    }
  }, [userID, showLayout, sessionFactory, location.pathname, navigate]);

  /* ---------------- ACCOUNT DELETION STATUS (ONCE PER LOGIN) ---------------- */
  useEffect(() => {
    if (!userID || !showLayout) return;

    const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
    const adminType = sessionFactory.get(SESSION_ADMIN_TYPE_KEY);
    const isAdminPanel =
      sessionFactory.get(SESSION_IS_SUPER_ADMIN_KEY) === true ||
      adminType === "SuperAdmin" ||
      adminType === "subAdmin" ||
      userType === "SuperAdmin" ||
      userType === "subAdmin";

    if (!isAdminLoggedIn(userType) || isAdminPanel) return;
    if (hasAccountDeletionPending(sessionFactory)) return;
    if (lastDeletionCheckUserRef.current === userID) return;

    let cancelled = false;

    const checkDeletionStatus = async () => {
      try {
        const result = await getAccountDeleteRequestStatus(userID);
        if (cancelled) return;

        if (result?.status_code !== 200 || !result.data) return;

        lastDeletionCheckUserRef.current = userID;
        const { status } = result.data;

        if (status === ACCOUNT_DELETION_STATUS.DELETED) {
          lastDeletionCheckUserRef.current = null;
          await clearAuthSession();
          navigate(`/${ROUTE_LOGIN}`, { replace: true });
          return;
        }

        if (isPendingDeletionStatus(status)) {
          setAccountDeletionSession(sessionFactory, {
            pending: true,
            data: result.data,
          });
          navigate(`/${ROUTE_DELETE_ACCOUNT}`, { replace: true });
        } else {
          clearAccountDeletionSession(sessionFactory);
        }
      } catch (error) {
        console.error("Account deletion status check failed:", error);
      }
    };

    checkDeletionStatus();

    return () => {
      cancelled = true;
    };
  }, [userID, showLayout, sessionFactory, navigate]);

  /* ---------------- DRAWER RESPONSIVE BEHAVIOR ---------------- */
  useEffect(() => {
    setIsDrawerOpen(!isMobile);
  }, [isMobile]);

  const toggleDrawer = () => {
    if (isMobile) {
      setIsDrawerOpen((prev) => !prev);
    }
  };

  const closeDrawer = () => {
    if (isMobile) {
      setIsDrawerOpen(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <>
      <UserNotFoundDialog
        open={Boolean(userNotFoundMessage)}
        message={userNotFoundMessage}
        onConfirm={handleUserNotFoundConfirm}
      />

      <FCMListener />

      {showLayout && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
          }}
        >
          <Navbar toggleDrawer={toggleDrawer} />
        </Box>
      )}

      {/* BODY */}
      <Box
        sx={{
          display: "flex",
          pt: showLayout ? `${LAYOUT_NAVBAR_HEIGHT}px` : 0,
        }}
      >
        {/* Mobile overlay: tapping outside drawer closes it so grids aren't stuck "behind" the pane. */}
        {showLayout && isMobile && isDrawerOpen && (
          <Box
            aria-hidden="true"
            onClick={closeDrawer}
            sx={{
              position: "fixed",
              inset: 0,
              top: `${LAYOUT_NAVBAR_HEIGHT}px`,
              zIndex: theme.zIndex.drawer - 1,
              bgcolor: "rgba(0,0,0,0.45)",
            }}
          />
        )}

        {showLayout && isDrawerOpen && (
          <Box
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              position: "fixed",
              top: `${LAYOUT_NAVBAR_HEIGHT}px`,
              left: 0,
              height: `calc(100vh - ${LAYOUT_NAVBAR_HEIGHT}px)`,
              backgroundColor: "#f8f9fa",
              borderRight: "1px solid #e0e0e0",
              zIndex: theme.zIndex.drawer,
            }}
          >
            <SidebarDrawer toggleDrawer={toggleDrawer} />
          </Box>
        )}

        {/* MAIN CONTENT — minWidth:0 lets overflow-x work inside flex row */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            ml:
              showLayout && !isMobile && isDrawerOpen
                ? `${DRAWER_WIDTH}px`
                : 0,
            transition: "margin 0.3s ease",
            backgroundColor: VM_Colors.white1,
            minHeight: showLayout
              ? `calc(100vh - ${LAYOUT_NAVBAR_HEIGHT}px)`
              : "100vh",
            width: "100%",
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: OUTLET_CONTENT_MAX_WIDTH,
              overflowX: "auto",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default App;
